




import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import * as d3 from "d3";
import moment from "moment";
import _ from "lodash";
import { formatCurrency } from "@/shared/currency.js";
import { GradientPoint, TransactionSource, HsaDailyBalance, RgbColor } from "@/shared/types";
import { tint, convertColorToHex, covertHexToRgbColor } from "@/shared/colorAdjuster";
import { useThemeStore } from "@/shared/store/theme";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";

const TooltipClass = "chart-tooltip";
const TooltipClassRight = "chart-tooltip-right";
const TooltipClassHeading = "chart-tooltip-type";
const TooltipClassAmountUp = "chart-tooltip-up";
const TooltipClassAmountDown = "chart-tooltip-down";

@Component
export default class Chart extends Vue {
  dailyBalanceStore = useHsaDailyBalanceStore();
  themeStore = useThemeStore();

  @Prop()
  classes: string;

  @Prop()
  negativeDataSet: any[];

  @Prop()
  positiveDataSet: HsaDailyBalance[];

  @Prop()
  valueSelector: (item: any) => any;

  @Prop()
  positiveDataSetFillColor: string;

  @Prop()
  positiveDataSetFillGradient: GradientPoint[];

  @Prop()
  positiveDataSetStrokeColor: string;

  @Prop()
  negativeDataSetStrokeColor: string;

  @Prop()
  negativeDataSetFillGradient: GradientPoint[];

  @Prop()
  positiveDataSetPrediction: HsaDailyBalance[];

  @Prop()
  baselineDataSet: HsaDailyBalance[];

  @Prop()
  height: number;

  @Prop()
  hasHovers: boolean;

  @Prop()
  tooltipGenerator: (item: any) => string;

  @Prop()
  isCompact: boolean;

  @Prop()
  displayMonthNames: boolean;

  @Prop()
  legend: {
    negativeDataSet,
    positiveDataSet,
    baselineDataSet
  };

  width = 0;
  margin = {
    top: 8,
    right: 8,
    bottom: 20,
    left: 50
  };
  isMobile = false;
  svg = null;

  get lightenedPrimaryColor (): string {
    const primaryColorRgb = covertHexToRgbColor(this.themeStore.primaryColor);
    const tintedColor = tint(primaryColorRgb.r, primaryColorRgb.g, primaryColorRgb.b, 0.6);

    return convertColorToHex(tintedColor);
  }

  renderChart (margin) {
    d3.selectAll(`#app .${TooltipClass}`).remove();
    const width = this.width - margin.left - margin.right;
    const height = this.height - margin.top - margin.bottom;
    const combinedData = this.negativeDataSet ? this.negativeDataSet.slice() : [];
    if (this.positiveDataSet) {
      combinedData.push(...this.positiveDataSet);
    }
    if (this.positiveDataSetPrediction) {
      combinedData.push(...this.positiveDataSetPrediction);
    }
    if (this.baselineDataSet) {
      combinedData.push(...this.baselineDataSet);
    }
    const parseTime = d3.timeParse("%d-%b-%y");

    const x = d3.scaleTime()
      .range([0, width])
      .domain(d3.extent(combinedData, d => parseTime(d.date)));
    const maxY = d3.max(combinedData, d => this.valueSelector(d));
    const isZeroChart = maxY === 0;
    const y = d3.scaleLinear()
      .range([height, 0])
      .domain([0, maxY < 5 ? 5 : maxY]);

    const legendHeight = this.legend ? this.height + 75 : this.height;
    const svg = d3.select(this.$el)
      .html("")
      .attr("width", this.width)
      .attr("height", legendHeight)
      .attr("viewBox", "0 0 " + this.width + " " + legendHeight)
      .append("g")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");
    this.svg = svg;

    let explicitTickValues = null;

    if (this.displayMonthNames) {
      // if set to explicitly display all month names, ensure that tick values are set
      // d3.timeMonth does not include months unless the first day of month is in the data set

      const firstDataPoint = moment(_.head(combinedData).date);
      const lastDataPoint = moment(_.last(combinedData).date);

      const firstDate = new Date(`01-${firstDataPoint.format("MMM")}-${firstDataPoint.year()}`);
      const lastDate = new Date(`${lastDataPoint.format("DD-MMM-YYYY")}`);

      explicitTickValues = d3.timeMonth.range(firstDate, lastDate);
    }

    this.addGradientDefs(svg);
    const isCompact = this.isCompact || this.width < 600;
    const isMinuscule = this.width < 300;

    const div = d3.select("#app").append("div")
      .attr("class", TooltipClass)
      .style("opacity", 0);
    svg.append("g")
      .attr("transform", "translate(-1," + height + ")")
      .attr("class", isCompact ? "compactAxis" : "xaxis")
      .call(d3.axisBottom(x)
        .ticks(d3.timeMonth)
        .tickValues(explicitTickValues)
        .tickFormat(
          date => {
            if (d3.timeYear(date) < date) {
              const month = d3.timeFormat("%b")(date);

              if (isMinuscule && !this.displayMonthNames) {
                return month[0];
              }

              return month;
            } else {
              const monthAndYear = d3.timeFormat("%b %Y")(date);

              if (isMinuscule && !this.displayMonthNames) {
                return monthAndYear[0] + d3.timeFormat("%y")(date);
              }

              return monthAndYear;
            }
          }
        ))
      .selectAll("line").remove();
    const yTicks = svg.append("g")
      .attr("transform", "translate(-1 ,0)")
      .attr("class", isCompact ? "compactAxis" : "yaxis")
      .call(d3.axisLeft(y).ticks(height < 150 || isCompact ? 2 : 4).tickFormat(d => "$" + parseInt(d)));

    yTicks.selectAll("line").remove();

    if (isZeroChart) {
      // move the y-axis tick down to the x-axis baseline
      yTicks.select("g").attr("transform", "translate(0 , " + height + ")");
    }

    const compactClass = isCompact ? " lineCompact" : "";

    if (this.baselineDataSet && this.baselineDataSet.length) {
      this.renderLineData(svg, this.baselineDataSet, "baselineDataSet" + compactClass, "dot3", "area3", x, y, height,
                          div, this.themeStore.primaryColor);
    }

    if (this.positiveDataSet && this.positiveDataSet.length && isZeroChart === false) {
      const firstPositiveBalance = _.head(this.positiveDataSet);
      const lastPositiveBalance = _.tail(this.positiveDataSet);
      // every balance has same date, resulting in vertical line that doesn't have line graph meaning
      // render a dot at highest point instead
      if (_.every(this.positiveDataSet, item => {
        return item.date === firstPositiveBalance.date;
      })) {
        this.renderSingleDataPoint(svg, [lastPositiveBalance], "positiveDataSet" + compactClass, "dot2", "area2", x, y, height,
                          div, this.positiveDataSetStrokeColor || "#00AF89");
      } else {
        this.renderLineData(svg, this.positiveDataSet, "positiveDataSet" + compactClass, "dot2", "area2", x, y, height,
                          div, this.positiveDataSetStrokeColor || "#00AF89");
      }
    }

    if (this.positiveDataSetPrediction && this.positiveDataSetPrediction.length) {
      if (this.positiveDataSet && this.positiveDataSetPrediction.length) {
        const lastElementIndex = this.positiveDataSet.length - 1;
        this.positiveDataSetPrediction.splice(0, 0, this.positiveDataSet[lastElementIndex]);
      }
      this.renderLineData(
        svg,
        this.positiveDataSetPrediction,
        "positiveDataSetPrediction" + compactClass,
        "dot2", "area2prediction",
        x,
        y,
        height,
        div,
        this.lightenedPrimaryColor);
    }

    if (this.negativeDataSet) {
      this.renderLineData(svg, this.negativeDataSet, "line" + compactClass, "dot",
        "area", x, y, height, div, this.themeStore.primaryColor);
    }

    if (this.legend) {
      const dist = d3.scaleLinear()
        .range([0, width])
        .domain([0, 2]);
      let i = 0;
      if (this.negativeDataSet) {
        this.renderLegend(svg, this.legend.negativeDataSet, dist(i), 0.7 * legendHeight, "#F0545E");
        i += 1;
      }
      if (this.positiveDataSet) {
        this.renderLegend(svg, this.legend.positiveDataSet, dist(i), 0.7 * legendHeight,
                          this.positiveDataSetStrokeColor || "#00AF89");
        i += 1;
      }
      if (this.baselineDataSet) {
        this.renderLegend(svg, this.legend.baselineDataSet, dist(i), 0.7 * legendHeight, "#4268D7");
        i += 1;
      }
    }
  }

  renderLegend (svg, legendDetails, posX, posY, color) {
    const legend = svg.append("g")
      .attr("transform", "translate(" + posX + "," + posY + ")");
    legend.append("text").text(legendDetails.text1)
      .attr("font-size", "12px");

    if (legendDetails.text2) {
      legend.append("text").text(legendDetails.text2)
        .attr("font-size", "14px")
        .attr("font-weight", "400")
        .attr("transform", "translate(" + 0 + "," + 20 + ")");
    }
    legend.append("circle")
      .attr("cx", -15)
      .attr("cy", -5)
      .attr("r", 5)
      .attr("fill", color);
  }

  @Watch("dailyBalanceStore.dailyBalancesTransactionsDictionary")
  updateTooltip (svg) {
    if (this.hasHovers) {
      this.svg.selectAll("svg circle:hover").dispatch("transactionsLoaded");
    }
  }

  renderSingleDataPoint (svg, data, lineClass, dotClass, areaClass, x, y, height, div, strokeColor) {
    const parseTime = d3.timeParse("%d-%b-%y");

    const valueline = d3.line()
      .x(d => x(parseTime(d.date)))
      .y(d => y(this.valueSelector(d)));

    const area = d3.area()
      .x(d => x(parseTime(d.date)))
      .y0(height)
      .y1(d => y(this.valueSelector(d)));
    const a = svg.selectAll("svg")
      .data([data]);
    a.enter()
      .append("path")
      .attr("d", area)
      .attr("fill", "url(#" + areaClass + ")")
      .style("pointer-events", "none");

    const d = svg.selectAll("svg")
      .data([data]);
    d.enter().append("path")
      .merge(d)
      .attr("class", lineClass)
      .attr("stroke", strokeColor)
      .attr("d", valueline);

    svg.selectAll("svg")
      .data(data)
      .enter().append("circle")
      .attr("class", dotClass)
      .attr("r", 5)
      .attr("cx", (d, i) => x(parseTime(d.date)))
      .attr("cy", (d, i) => y(this.valueSelector(d)));
  }

  renderLineData (svg, data, lineClass, dotClass, areaClass, x, y, height, div, strokeColor) {
    const parseTime = d3.timeParse("%d-%b-%y");

    const valueline = d3.line()
      .x(d => x(parseTime(d.date)))
      .y(d => y(this.valueSelector(d)));

    const area = d3.area()
      .x(d => x(parseTime(d.date)))
      .y0(height)
      .y1(d => y(this.valueSelector(d)));
    const a = svg.selectAll("svg")
      .data([data]);
    a.enter()
      .append("path")
      .attr("d", area)
      .attr("fill", "url(#" + areaClass + ")")
      .style("pointer-events", "none");

    const d = svg.selectAll("svg")
      .data([data]);
    d.enter().append("path")
      .merge(d)
      .attr("class", lineClass)
      .attr("stroke", strokeColor)
      .attr("d", valueline);

    if (!this.isMobile && this.hasHovers) {
      const getTooltip = this.getTooltip;
      svg.selectAll("svg")
        .data(data)
        .enter().append("circle")
        .attr("class", dotClass)
        .attr("r", 5)
        .attr("cx", (d, i) => x(parseTime(d.date)))
        .attr("cy", (d, i) => y(this.valueSelector(d)))
        .on("transactionsLoaded", d => {
          if ((window.innerWidth - d3.event.pageX) > 100) {
            div.html(getTooltip(d))
              .attr("class", TooltipClass);
          }
          else {
            div.html(getTooltip(d))
              .attr("class", TooltipClass);
          }
        })
        .on("mouseover", d => {
          div.transition()
            .duration(200)
            .style("opacity", 1);
          if ((window.innerWidth - d3.event.pageX) > 100) {
            div.html(getTooltip(d))
              .attr("class", TooltipClass)
              .style("left", (d3.event.pageX + 12) + "px")
              .style("right", "auto")
              .style("top", (d3.event.pageY - 63) + "px");
          }
          else {
            div.html(getTooltip(d))
              .attr("class", `${TooltipClass} ${TooltipClassRight}`)
              .style("left", "auto")
              .style("right", ((window.innerWidth - d3.event.pageX) + 15) + "px")
              .style("top", (d3.event.pageY - 63) + "px");
          }
        })
        .on("mouseout", d => {
          div.transition()
            .duration(500)
            .style("opacity", 0);
          });
    }
  }

  getTooltip (item) {
    const dateKey = moment(item.date, "DD-MMM-YY").format("YYYYMMDD");
    const requestDate = moment(item.date, "DD-MMM-YY").format("YYYY-MM-DD");
    this.dailyBalanceStore.setTooltipDate(dateKey);

    if (this.tooltipGenerator) {
      return this.tooltipGenerator(item);
    }

    if (this.dailyBalanceStore.transactionsForCurrentDate) {
      if (_.has(item, "employerAmount")) {
        return this.getMarkupForPredictedContribution(item);
      } else {
        return this.getMarkupForDailyBalance(item);
      }
    } else {
      this.dailyBalanceStore.loadTransactionsForDay(requestDate);
    }
  }

  getMarkupForDailyBalance (item) {
    let groupedTransactions = {};
    this.dailyBalanceStore.transactionsForCurrentDate.forEach(transaction => {
      if (groupedTransactions[transaction.source]) {
        groupedTransactions[transaction.source].amount += transaction.amount;
      } else {
        let displaySource = transaction.displaySource;
        if (transaction.source === TransactionSource.PersonalContribution) {
          displaySource = "Personal Contribution";
        }
        groupedTransactions[transaction.source] = {
          displaySource,
          amount: transaction.amount,
        };
      }
    });
    groupedTransactions = _.orderBy(groupedTransactions, ["amount"], ["desc"]);

    let markup = this.getBalanceDateHeaderMarkup(item);
    for (const key in groupedTransactions) {
      const item = groupedTransactions[key];
      const heading = `<h3 class="${TooltipClassHeading}">${item.displaySource}</h3>`;
      const balance = `<div class="color-primary">
        ${formatCurrency(item.amount)}
      </div>`;

      markup += `${heading}${balance}`;
    }

    return `${markup}`;
  }

  getBalanceDateHeaderMarkup (item) {
    const date = moment(item.date, this.dailyBalanceStore.currentDateFormat).format("MMM DD");

    return `<h3 class="${TooltipClassHeading}">${date}</h3>
     <h3 class="${TooltipClassHeading}">Balance</h3>
     <div class="color-primary">
         ${formatCurrency(item.balance)}
     </div>`;
  }

  getMarkupForPredictedContribution (item) {
    let markup = this.getBalanceDateHeaderMarkup(item);
    markup += this.getPredictionDetailMarkup(item.employerAmount, "Employer Payroll");
    markup += this.getPredictionDetailMarkup(item.employeeRecurringAmount, "Employee Payroll");
    markup += this.getPredictionDetailMarkup(item.employeeOneTimeAmount, "Payroll One Time");

    return `${markup}`;
  }

  getPredictionDetailMarkup (balance: number, description: string) {
    if (balance) {
      const heading = `<h3 class="${TooltipClassHeading}">${description}</h3>`;
      const balanceMarkup = `<div class="color-primary">${formatCurrency(balance)}</div>`;

      return `${heading}${balanceMarkup}`;
    }

    return "";
  }

  handleResize () {
    this.width = this.$el.getBoundingClientRect().width;
    if (this.width >= 767 && !this.isCompact) {
      this.isMobile = false;
      this.renderChart(this.margin);
    } else {
      this.isMobile = true;
      this.renderChart(this.margin);
    }
  }

  addGradientDefs (svg) {
    const defs = svg.append("defs");

    const gradient1 = defs.append("linearGradient")
      .attr("id", "area")
      .attr("x1", "54.121%")
      .attr("x2", "54.121%")
      .attr("y1", "100%")
      .attr("y2", "0%");


    let negativeStartGradient: GradientPoint = {
      alpha: 0.8,
      color: "#D8F2E9"
    };
    if (this.negativeDataSetFillGradient && this.negativeDataSetFillGradient.length) {
      negativeStartGradient = this.negativeDataSetFillGradient[0];
    }
    gradient1.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", negativeStartGradient.color)
      .attr("stop-opacity", negativeStartGradient.alpha);

    let negativeEndGradient: GradientPoint = {
      alpha: 0.8,
      color: "#B0E0CE"
    };
    if (this.negativeDataSetFillGradient && this.negativeDataSetFillGradient.length > 1) {
      negativeEndGradient = this.negativeDataSetFillGradient[1];
    }
    gradient1.append("stop")
      .attr("class", "end")
      .attr("offset", "100%")
      .attr("stop-color", negativeEndGradient.color)
      .attr("stop-opacity", negativeEndGradient.alpha);

    const gradient2 = defs.append("linearGradient")
      .attr("id", "area2")
      .attr("x1", "50%")
      .attr("x2", "50%")
      .attr("y1", "100%")
      .attr("y2", "0%");

    let positiveStartGradient: GradientPoint = {
      alpha: 0.8,
      color: "#D8F2E9"
    };
    if (this.positiveDataSetFillGradient && this.positiveDataSetFillGradient.length) {
      positiveStartGradient = this.positiveDataSetFillGradient[0];
    }
    gradient2.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", positiveStartGradient.color)
      .attr("stop-opacity", positiveStartGradient.alpha);

    let positiveEndGradient: GradientPoint = {
      alpha: 0.8,
      color: "#B0E0CE"
    };
    if (this.positiveDataSetFillGradient && this.positiveDataSetFillGradient.length > 1) {
      positiveEndGradient = this.positiveDataSetFillGradient[1];
    }
    gradient2.append("stop")
      .attr("class", "end")
      .attr("offset", "100%")
      .attr("stop-color", positiveEndGradient.color)
      .attr("stop-opacity", positiveEndGradient.alpha);

    defs.append("linearGradient")
      .attr("id", "area2prediction");

    const gradient3 = defs.append("linearGradient")
      .attr("id", "area3")
      .attr("x1", "50%")
      .attr("x2", "50%")
      .attr("y1", "100%")
      .attr("y2", "0%");

    gradient3.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#4268D7")
      .attr("stop-opacity", .2);

    gradient3.append("stop")
      .attr("class", "end")
      .attr("offset", "100%")
      .attr("stop-color", "#4268D7")
      .attr("stop-opacity", .6);
  }

  mounted () {
    this.width = this.$el.getBoundingClientRect().width;
    window.addEventListener("resize", this.handleResize);
    if (this.width >= 767 && !this.isCompact) {
      this.renderChart(this.margin);
    } else {
      this.isMobile = true;
      this.renderChart(this.margin);
    }
  }

  @Watch("$props", { deep: true })
  redrawOnPropsChange () {
    this.renderChart(this.margin);
  }

  beforeDestroy () {
    window.removeEventListener("resize", this.handleResize);
    d3.selectAll(`#app .${TooltipClass}`).remove();
  }
}
